import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  to?: string;
  component?: React.ElementType;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      fullWidth = false,
      startIcon,
      endIcon,
      children,
      className,
      to,
      component,
      ...props
    },
    ref
  ) => {
    const location = useLocation();
    
    const baseClasses = clsx(
      'inline-flex items-center justify-center gap-2 font-medium rounded transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      'cursor-pointer select-none no-underline',
      {
        // Size variants
        'px-3 py-1.5 text-sm': size === 'small',
        'px-4 py-2 text-base': size === 'medium',
        'px-6 py-3 text-lg': size === 'large',
        
        // Full width
        'w-full': fullWidth,
      }
    );

    const variantClasses = clsx({
      // Contained variants
      'bg-primary text-white hover:bg-primary-600 focus:ring-primary-500': 
        variant === 'contained' && color === 'primary',
      'bg-secondary text-white hover:bg-secondary-600 focus:ring-secondary-500': 
        variant === 'contained' && color === 'secondary',
      'bg-success text-white hover:bg-success-600 focus:ring-success-500': 
        variant === 'contained' && color === 'success',
      'bg-error text-white hover:bg-error-600 focus:ring-error-500': 
        variant === 'contained' && color === 'error',
      'bg-warning text-white hover:bg-warning-600 focus:ring-warning-500': 
        variant === 'contained' && color === 'warning',
      'bg-info text-white hover:bg-info-600 focus:ring-info-500': 
        variant === 'contained' && color === 'info',

      // Outlined variants
      'border-2 border-primary text-primary hover:bg-primary-50 focus:ring-primary-500': 
        variant === 'outlined' && color === 'primary',
      'border-2 border-secondary text-secondary hover:bg-secondary-50 focus:ring-secondary-500': 
        variant === 'outlined' && color === 'secondary',
      'border-2 border-success text-success hover:bg-success-50 focus:ring-success-500': 
        variant === 'outlined' && color === 'success',
      'border-2 border-error text-error hover:bg-error-50 focus:ring-error-500': 
        variant === 'outlined' && color === 'error',
      'border-2 border-warning text-warning hover:bg-warning-50 focus:ring-warning-500': 
        variant === 'outlined' && color === 'warning',
      'border-2 border-info text-info hover:bg-info-50 focus:ring-info-500': 
        variant === 'outlined' && color === 'info',

      // Text variants
      'text-primary hover:bg-primary-50 focus:ring-primary-500': 
        variant === 'text' && color === 'primary',
      'text-secondary hover:bg-secondary-50 focus:ring-secondary-500': 
        variant === 'text' && color === 'secondary',
      'text-success hover:bg-success-50 focus:ring-success-500': 
        variant === 'text' && color === 'success',
      'text-error hover:bg-error-50 focus:ring-error-500': 
        variant === 'text' && color === 'error',
      'text-warning hover:bg-warning-50 focus:ring-warning-500': 
        variant === 'text' && color === 'warning',
      'text-info hover:bg-info-50 focus:ring-info-500': 
        variant === 'text' && color === 'info',
    });

    const classes = clsx(baseClasses, variantClasses, className);

    // If "to" prop is provided, render as Link with navigation state
    if (to) {
      return (
        <Link 
          to={to} 
          state={{ from: location.pathname }}
          className={classes}
        >
          {startIcon && <span className="inline-flex">{startIcon}</span>}
          {children}
          {endIcon && <span className="inline-flex">{endIcon}</span>}
        </Link>
      );
    }

    // If component prop is provided, use it
    const Component = component || 'button';

    return (
      <Component ref={ref} className={classes} {...props}>
        {startIcon && <span className="inline-flex">{startIcon}</span>}
        {children}
        {endIcon && <span className="inline-flex">{endIcon}</span>}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export default Button;

